<script setup lang="ts">
/**
 * ProductViewDialog
 *
 * Read-only tabbed dialog for viewing a product's full details.
 * Matches the product detail screenshot layout:
 *  - Header: product name + SKU + Edit button + X close
 *  - Left column: product image area with image search links
 *  - Right column: tabbed content (Overview, Nutrition, Vitamins, Minerals,
 *    Amino Acids, Packaging, Other, History)
 *
 * Edit button opens the AddProductDialog in edit mode with prefilled data.
 */
import { ref, computed, watch, onUnmounted } from 'vue';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import FileUpload from 'primevue/fileupload';
import { DialogModal, Icon, useToaster, useTimezone } from '@tms-phoenix/ui-vue';
import { REGION_FOR_USE_OPTIONS } from '@tms-phoenix/tms-types';
import { getApiErrorMessage } from '@/core/utils/api-error.util';
import { useProductsStore } from '@/stores/products.store';
import { fetchProductImageProxy } from '../services/products.api';
import type { Product } from '../types';
import type { ProductRegistrationPayload } from '../types/product-form';

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const MAX_IMAGE_SIZE_MB = 5;
const MAX_IMAGE_SIZE_BYTES = MAX_IMAGE_SIZE_MB * 1024 * 1024;

const props = defineProps<{
  visible: boolean;
  product: Product | null;
  readonly?: boolean;
}>();

const emit = defineEmits<{
  'update:visible': [value: boolean];
  'edit': [product: Product];
  'image-updated': [productId: string, imageUrl: string];
}>();

const productsStore = useProductsStore();
const toast = useToaster();
const { formatDate } = useTimezone();

// -- Proxied image URL ------------------------------------------
/**
 * The image URL to use in the <img> tag.
 * Images are fetched via axios (which carries the Bearer token) and exposed
 * as a blob: URL so the <img> tag never makes an unauthenticated request.
 */
const proxiedImageUrl = ref<string | null>(null);

/** Tracks the current blob URL so it can be revoked when replaced. */
let _blobObjectUrl: string | null = null;

function _revokeBlobUrl() {
  if (_blobObjectUrl) {
    URL.revokeObjectURL(_blobObjectUrl);
    _blobObjectUrl = null;
  }
}

function _setBlobUrl(blob: Blob | File) {
  _revokeBlobUrl();
  const url = URL.createObjectURL(blob);
  _blobObjectUrl = url;
  proxiedImageUrl.value = url;
}

onUnmounted(() => {
  _revokeBlobUrl();
});

watch(
  () => props.product?.imageUrl,
  async (url, _prev, onInvalidate) => {
    // Guard against stale responses when the product changes before a previous
    // fetch resolves: the cleanup function marks this invocation as cancelled so
    // the resolved value is never applied to the DOM.
    let cancelled = false;
    onInvalidate(() => { cancelled = true; });

    _revokeBlobUrl();
    if (!url) {
      proxiedImageUrl.value = null;
      return;
    }
    // Skip placeholder URLs (e.g. https://example.com/) — no real image to fetch
    try {
      if (new URL(url).hostname === 'example.com') {
        proxiedImageUrl.value = null;
        return;
      }
    } catch { /* invalid URL — let fetchProductImageProxy handle it */ }
    try {
      const response = await fetchProductImageProxy(url);
      if (!cancelled) {
        _setBlobUrl(response.data);
      }
    } catch {
      if (!cancelled) {
        proxiedImageUrl.value = null;
      }
    }
  },
  { immediate: true },
);

// -- Tabs ------------------------------------------------------
const VIEW_TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'nutrition', label: 'Nutrition' },
  { key: 'vitamins', label: 'Vitamins' },
  { key: 'minerals', label: 'Minerals' },
  { key: 'amino_acids', label: 'Amino Acids' },
  { key: 'packaging', label: 'Packaging' },
  { key: 'other', label: 'Other' },
  { key: 'history', label: 'History' },
] as const;

type TabKey = (typeof VIEW_TABS)[number]['key'];
const activeTab = ref<TabKey>('overview');

// -- Registration data shortcut ---------------------------------
const regData = computed<ProductRegistrationPayload | undefined>(
  () => (props.product as any)?.registrationData,
);

// Display helpers
// Several sections (Fat, Carbohydrates, Serving Information, Vitamins, Minerals,
// Amino Acids) render generically via `v-for` over a whole object and call `val()`
// on every field, so it must also handle the multi-select array fields (SV-390)
// mixed in among plain strings — joined for readability, "--" when empty.
function val(v: string | string[] | undefined | null): string {
  if (Array.isArray(v)) return v.length ? v.join(', ') : '--';
  return v && v.trim() ? v : '--';
}

/** Like val, but resolves region shortcodes (e.g. "US") to their full name for display. */
function regionVal(v: string[] | string | undefined | null): string {
  const resolve = (code: string) => REGION_FOR_USE_OPTIONS.find((o) => o.value === code)?.label ?? code;
  if (Array.isArray(v)) return v.length ? v.map(resolve).join(', ') : '--';
  return v ? resolve(v) : '--';
}

/** Formats an expiration value with its unit, e.g. "48 hours" — "--" when there's no value. */
function expirationVal(v: string | undefined | null, unit: string | undefined | null): string {
  return v && v.trim() ? `${v.trim()} ${unit ?? ''}`.trim() : '--';
}

function close() {
  activeTab.value = 'overview';
  emit('update:visible', false);
}

function openEdit() {
  if (props.product) {
    emit('edit', props.product);
  }
}

// -- Image upload -----------------------------------------------
const isUploadingImage = ref(false);
const isDraggingImage = ref(false);
const showUrlDialog = ref(false);
const imageUrlInput = ref('');

function onImageFileSelected(e: Event) {
  const target = e.target as HTMLInputElement;
  if (target.files && target.files.length > 0) {
    uploadImageFile(target.files[0]);
  }
  // Reset so the same file can be re-selected
  target.value = '';
}

function onImageDragOver(e: DragEvent) {
  e.preventDefault();
  isDraggingImage.value = true;
}

function onImageDragLeave() {
  isDraggingImage.value = false;
}

function onImageDrop(e: DragEvent) {
  e.preventDefault();
  isDraggingImage.value = false;
  if (isUploadingImage.value) return;
  const file = e.dataTransfer?.files?.[0];
  if (file) uploadImageFile(file);
}

function validateImageFile(file: File): string | null {
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    return `Invalid file type. Allowed: JPEG, PNG, WebP, GIF.`;
  }
  if (file.size > MAX_IMAGE_SIZE_BYTES) {
    return `File too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max: ${MAX_IMAGE_SIZE_MB} MB.`;
  }
  return null;
}

async function uploadImageFile(file: File) {
  if (!props.product || isUploadingImage.value) return;

  const validationError = validateImageFile(file);
  if (validationError) {
    toast.danger(validationError);
    return;
  }

  isUploadingImage.value = true;
  try {
    const imageUrl = await productsStore.uploadImage(props.product.id, file);
    if (imageUrl) {
      toast.success('Image uploaded successfully.');
      // Display the uploaded file immediately via a local blob URL
      _setBlobUrl(file);
      emit('image-updated', props.product.id, imageUrl);
    } else {
      toast.danger(productsStore.error ?? 'Failed to upload image.');
    }
  } finally {
    isUploadingImage.value = false;
  }
}

function openUrlDialog() {
  imageUrlInput.value = '';
  showUrlDialog.value = true;
}

async function submitImageUrl() {
  if (!props.product || !imageUrlInput.value.trim()) return;

  // Capture product id synchronously before any awaits — the parent could
  // null-out the product prop while the network request is in flight.
  const productId = props.product.id;
  const url = imageUrlInput.value.trim();

  // Client-side URL validation as a UX guard to reduce SSRF risk.
  // Real enforcement must happen server-side on the proxy endpoint.
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      toast.danger('Only http and https URLs are allowed.');
      return;
    }

    // Block localhost, loopback, link-local, and private IP ranges.
    // Private-range checks are only applied to IP literals (not hostnames)
    // to avoid false positives on domains like fda.gov or 10.com.
    const host = parsed.hostname.toLowerCase();
    const BLOCKED_HOSTS = ['localhost', '0.0.0.0'];

    if (BLOCKED_HOSTS.includes(host)) {
      toast.danger('URLs pointing to internal/private addresses are not allowed.');
      return;
    }

    // IPv4 literal check
    const IPV4_PATTERN = /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/;
    if (IPV4_PATTERN.test(host)) {
      const PRIVATE_IPV4 =
        /^(127\.|10\.|172\.(1[6-9]|2\d|3[01])\.|192\.168\.|169\.254\.|0\.)/;
      if (PRIVATE_IPV4.test(host)) {
        toast.danger('URLs pointing to internal/private addresses are not allowed.');
        return;
      }
    }

    // IPv6 literal check (parsed.hostname strips the brackets)
    const IPV6_PRIVATE = /^(::1$|fc[0-9a-f]{2}:|fd[0-9a-f]{2}:|fe80:)/i;
    if (host.includes(':') && IPV6_PRIVATE.test(host)) {
      toast.danger('URLs pointing to internal/private addresses are not allowed.');
      return;
    }
  } catch {
    toast.danger('Invalid URL. Please enter a valid image URL.');
    return;
  }

  // Fetch the image through the backend proxy to avoid CORS issues
  isUploadingImage.value = true;
  showUrlDialog.value = false;
  try {
    const response = await fetchProductImageProxy(url);
    const blob = response.data;

    // Reject if the response has no recognisable image content-type rather than
    // defaulting to JPEG, which would bypass validateImageFile's type check.
    if (!blob.type || !ALLOWED_IMAGE_TYPES.includes(blob.type)) {
      toast.danger(
        `The URL did not return a supported image type. Allowed: JPEG, PNG, WebP, GIF.`,
      );
      return;
    }

    // Determine filename from URL
    const urlPath = new URL(url).pathname;
    const filename = urlPath.split('/').pop() || 'image.jpg';
    const file = new File([blob], filename, { type: blob.type });

    const validationError = validateImageFile(file);
    if (validationError) {
      toast.danger(validationError);
      return;
    }

    const imageUrlResult = await productsStore.uploadImage(productId, file);
    if (imageUrlResult) {
      toast.success('Image uploaded successfully from URL.');
      _setBlobUrl(file);
      emit('image-updated', productId, imageUrlResult);
    } else {
      toast.danger(productsStore.error ?? 'Failed to upload image.');
    }
  } catch (err) {
    toast.danger(getApiErrorMessage(err, 'Failed to fetch image from URL. Check the URL and try again.'));
  } finally {
    isUploadingImage.value = false;
  }
}

// -- Image search helpers ---------------------------------------
function searchQuery(): string {
  if (!props.product) return '';
  return encodeURIComponent(`${props.product.brand} ${props.product.sku}`);
}

/**
 * Re-fetch the product image through the backend proxy and update the blob URL.
 * Called when the initial load fails, giving the user a manual retry path.
 */
async function retryImageFetch() {
  const url = props.product?.imageUrl;
  if (!url) return;
  try {
    const response = await fetchProductImageProxy(url);
    _setBlobUrl(response.data);
  } catch {
    proxiedImageUrl.value = null;
  }
}
</script>

<template>
  <DialogModal
    :visible="visible"
    :title="product?.brand ?? 'Product'"
    max-width="max-w-6xl"
    max-height="90vh"
    :no-padding="true"
    :show-footer="false"
    @update:visible="emit('update:visible', $event)"
    @close="close"
  >
    <!-- Custom header -->
    <template #header>
      <div class="flex flex-1 items-start justify-between">
        <div class="min-w-0">
          <h2 class="text-lg font-semibold text-gray-900 truncate">
            {{ product?.brand ?? '' }}
          </h2>
          <p class="text-sm text-gray-500">
            SKU: {{ product?.sku ?? '' }}
          </p>
        </div>
        <Button
          v-if="!readonly"
          severity="primary"
          class="rounded-xl! shrink-0 ml-4"
          @click="openEdit"
        >
          <Icon
            name="Edit"
            size-class="h-4 w-4"
            color="text-white"
          />
          <span>Edit</span>
        </Button>
      </div>
    </template>

    <!-- Body: two-column layout -->
    <div class="flex flex-1 min-h-0 overflow-hidden" style="min-height: 60vh;">
      <!-- Left sidebar: Image area -->
      <div class="shrink-0 w-64 border-r border-gray-200 p-4 flex flex-col items-center gap-4 overflow-y-auto">
        <!-- Image area (click to upload, drag-and-drop) -->
        <FileUpload
          mode="advanced"
          accept="image/jpeg,image/png,image/webp,image/gif"
          :multiple="false"
          :disabled="readonly"
          :show-upload-button="false"
          :show-cancel-button="false"
          custom-upload
          unstyled
          :pt="{ input: { class: 'hidden' }, content: { class: 'hidden' } }"
          @select="(e: { originalEvent: Event }) => onImageFileSelected(e.originalEvent)"
        >
          <template #header="{ chooseCallback }">
            <div
              class="w-full aspect-square rounded-lg border-2 bg-gray-50 flex flex-col items-center justify-center gap-2 transition-colors relative"
              :class="[
                readonly ? 'border-gray-200 border-solid' : 'cursor-pointer',
                !readonly && isDraggingImage ? 'border-primary bg-primary-50 border-solid' : !readonly ? 'border-gray-200 border-dashed hover:border-gray-300' : '',
              ]"
              :tabindex="readonly ? -1 : 0"
              :role="readonly ? undefined : 'button'"
              :aria-label="readonly ? undefined : 'Upload product image'"
              @click="!readonly && chooseCallback()"
              @keydown.enter.prevent="!readonly && chooseCallback()"
              @keydown.space.prevent="!readonly && chooseCallback()"
              @dragover="!readonly && onImageDragOver($event)"
              @dragleave="!readonly && onImageDragLeave()"
              @drop="!readonly && onImageDrop($event)"
            >
              <!-- Loading overlay -->
              <div
                v-if="isUploadingImage"
                class="absolute inset-0 bg-white/70 rounded-lg flex items-center justify-center z-10"
              >
                <div class="flex flex-col items-center gap-2">
                  <div class="h-6 w-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  <p class="text-xs text-gray-500">Uploading...</p>
                </div>
              </div>

              <img
                v-if="proxiedImageUrl"
                :src="proxiedImageUrl"
                :alt="product?.sku"
                class="w-full h-full object-contain p-2"
              />
              <template v-else>
                <Icon
                  name="MilkInventory"
                  size-class="h-12 w-12"
                  color="text-gray-500"
                />
                <p class="text-sm text-gray-400">
                  No image yet
                </p>
                <p v-if="!readonly" class="text-xs text-gray-400">
                  Click or drag to upload
                </p>
              </template>
            </div>

            <Button
              v-if="!readonly"
              severity="secondary"
              outlined
              class="w-full! justify-center! rounded-lg! text-sm!"
              :disabled="isUploadingImage"
              @click="chooseCallback"
            >
              <Icon
                name="Upload"
                size-class="h-3.5 w-3.5"
                color="gray"
                variant="stroke"
              />
              <span>Upload Image</span>
            </Button>
          </template>
          <template #content></template>
        </FileUpload>

        <!-- Write actions (hidden in readonly mode) -->
        <template v-if="!readonly">
          <!-- Search Images -->
          <div class="w-full space-y-2">
            <p class="text-xs font-medium text-gray-500">
              Search Images:
            </p>
            <div class="flex gap-2">
              <a
                :href="`https://duckduckgo.com/?q=${searchQuery()}&iax=images&ia=images`"
                target="_blank"
                rel="noopener"
                class="flex-1 text-center text-xs py-1.5 px-2 rounded border border-gray-200 hover:bg-gray-50 text-gray-700 flex flex-col items-center gap-0.5"
              >
                <Icon name="OpenInNewTab" size-class="h-3 w-3" color="gray" />
                DuckDuckGo
              </a>
              <a
                :href="`https://www.google.com/search?q=${searchQuery()}&tbm=isch`"
                target="_blank"
                rel="noopener"
                class="flex-1 text-center text-xs py-1.5 px-2 rounded border border-gray-200 hover:bg-gray-50 text-gray-700 flex flex-col items-center gap-0.5"
              >
                <span class="font-bold text-blue-600">G</span> Google
              </a>
              <a
                :href="`https://www.bing.com/images/search?q=${searchQuery()}`"
                target="_blank"
                rel="noopener"
                class="flex-1 text-center text-xs py-1.5 px-2 rounded border border-gray-200 hover:bg-gray-50 text-gray-700 flex flex-col items-center gap-0.5"
              >
                <Icon name="OpenInNewTab" size-class="h-3 w-3" color="gray" />
                Bing
              </a>
            </div>

            <Button
              severity="secondary"
              outlined
              class="w-full! justify-center! rounded-lg! text-sm!"
              :disabled="isUploadingImage"
              @click="openUrlDialog"
            >
              <Icon
                name="LinkChain"
                size-class="h-3.5 w-3.5"
                color="gray"
                variant="stroke"
              />
              <span>Enter URL</span>
            </Button>

            <Button
              severity="secondary"
              outlined
              class="w-full! justify-center! rounded-lg! text-sm!"
              :disabled="isUploadingImage || !product?.imageUrl"
              @click="retryImageFetch"
            >
              <Icon
                name="ClockArrow"
                size-class="h-3.5 w-3.5"
                color="gray"
              />
              <span>Retry Fetch</span>
            </Button>
          </div>
        </template>
      </div>

      <!-- Right: Tabbed content -->
      <div class="flex-1 flex flex-col min-h-0 min-w-0">
        <!-- Tab bar -->
        <div class="shrink-0 border-b border-gray-200 px-4 flex gap-1 overflow-x-auto">
          <button
            v-for="tab in VIEW_TABS"
            :key="tab.key"
            class="px-3 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors"
            :class="activeTab === tab.key
              ? 'border-primary text-primary'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'"
            @click="activeTab = tab.key"
          >
            {{ tab.label }}
          </button>
        </div>

        <!-- Tab content (scrollable) -->
        <div class="flex-1 overflow-y-auto p-5 space-y-6">
          <!-- === OVERVIEW === -->
          <template v-if="activeTab === 'overview'">
            <!-- Basic Information -->
            <section>
              <h3 class="text-sm font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-200">
                Basic Information
              </h3>
              <div class="grid grid-cols-2 gap-x-8 gap-y-4">
                <div>
                  <p class="text-xs font-semibold uppercase tracking-wide text-gray-400">SKU</p>
                  <p class="mt-0.5 text-sm text-gray-800">{{ val(regData?.overview?.basic_information?.sku ?? product?.sku) }}</p>
                </div>
                <div>
                  <p class="text-xs font-semibold uppercase tracking-wide text-gray-400">BRAND</p>
                  <p class="mt-0.5 text-sm text-gray-800">{{ val(regData?.overview?.basic_information?.brand_name ?? product?.brand) }}</p>
                </div>
                <div>
                  <p class="text-xs font-semibold uppercase tracking-wide text-gray-400">MANUFACTURER</p>
                  <p class="mt-0.5 text-sm text-gray-800">{{ val(regData?.overview?.basic_information?.manufacturer ?? product?.manufacturer) }}</p>
                </div>
                <div>
                  <p class="text-xs font-semibold uppercase tracking-wide text-gray-400">FORM</p>
                  <p class="mt-0.5 text-sm text-gray-800">{{ val(regData?.overview?.basic_information?.form ?? product?.form) }}</p>
                </div>
                <div>
                  <p class="text-xs font-semibold uppercase tracking-wide text-gray-400">CALORIES FOR LIQUIDS (KCAL/mL)</p>
                  <p class="mt-0.5 text-sm text-gray-800">{{ val(regData?.overview?.basic_information?.calories_for_liquids) }}</p>
                </div>
                <div>
                  <p class="text-xs font-semibold uppercase tracking-wide text-gray-400">CALORIES FOR PWDS (KCAL/G)</p>
                  <p class="mt-0.5 text-sm text-gray-800">{{ val(regData?.overview?.basic_information?.calories_for_pwds) }}</p>
                </div>
                <div>
                  <p class="text-xs font-semibold uppercase tracking-wide text-gray-400">DATE VERIFIED</p>
                  <p class="mt-0.5 text-sm text-gray-800">{{ val(regData?.overview?.basic_information?.date_verified) }}</p>
                </div>
                <div v-if="regData?.overview?.basic_information?.type_of_product">
                  <p class="text-xs font-semibold uppercase tracking-wide text-gray-400">TYPE OF PRODUCT</p>
                  <p class="mt-0.5 text-sm text-gray-800">{{ val(regData.overview.basic_information.type_of_product) }}</p>
                </div>
                <div v-if="regData?.overview?.basic_information?.flavor">
                  <p class="text-xs font-semibold uppercase tracking-wide text-gray-400">FLAVOR</p>
                  <p class="mt-0.5 text-sm text-gray-800">{{ val(regData.overview.basic_information.flavor) }}</p>
                </div>
                <!-- <div v-if="regData?.overview?.basic_information?.product_status">
                  <p class="text-xs font-semibold uppercase tracking-wide text-gray-400">PRODUCT STATUS</p>
                  <p class="mt-0.5 text-sm text-gray-800">{{ val(regData.overview.basic_information.product_status) }}</p>
                </div> -->
                <div v-if="regData?.overview?.basic_information?.hcpcs">
                  <p class="text-xs font-semibold uppercase tracking-wide text-gray-400">HCPCS</p>
                  <p class="mt-0.5 text-sm text-gray-800">{{ val(regData.overview.basic_information.hcpcs) }}</p>
                </div>
                <div v-if="regData?.overview?.basic_information?.ndc_format_code || product?.ndc">
                  <p class="text-xs font-semibold uppercase tracking-wide text-gray-400">NDC FORMAT CODE</p>
                  <p class="mt-0.5 text-sm text-gray-800">{{ val(regData?.overview?.basic_information?.ndc_format_code ?? product?.ndc) }}</p>
                </div>
                <div v-if="regData?.overview?.basic_information?.age_for_use?.length">
                  <p class="text-xs font-semibold uppercase tracking-wide text-gray-400">AGE FOR USE</p>
                  <p class="mt-0.5 text-sm text-gray-800">{{ val(regData.overview.basic_information.age_for_use) }}</p>
                </div>
                <div v-if="regData?.overview?.basic_information?.region_for_use?.length">
                  <p class="text-xs font-semibold uppercase tracking-wide text-gray-400">REGION FOR USE</p>
                  <p class="mt-0.5 text-sm text-gray-800">{{ regionVal(regData.overview.basic_information.region_for_use) }}</p>
                </div>
              </div>
            </section>

            <section v-if="regData?.packaging?.primary_package">
              <h3 class="text-sm font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-200">
                Primary Package
              </h3>
              <div class="grid grid-cols-2 gap-x-8 gap-y-3">
                <div v-for="(value, key) in regData.packaging.primary_package" :key="key">
                  <p class="text-xs font-semibold uppercase tracking-wide text-gray-400">{{ String(key).replace(/_/g, ' ') }}</p>
                  <p class="mt-0.5 text-sm text-gray-800">{{ val(String(value ?? '')) }}</p>
                </div>
              </div>
            </section>

            <!-- Expiration -->
            <section>
              <h3 class="text-sm font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-200">
                Expiration
              </h3>
              <div class="grid grid-cols-2 gap-x-8 gap-y-3">
                <div>
                  <p class="text-xs font-semibold uppercase tracking-wide text-gray-400">EXPIRATION AFTER MIXING</p>
                  <p class="mt-0.5 text-sm text-gray-800">{{ expirationVal(regData?.overview?.expiration?.expiration_after_mixing, regData?.overview?.expiration?.expiration_after_mixing_unit) }}</p>
                </div>
                <div>
                  <p class="text-xs font-semibold uppercase tracking-wide text-gray-400">EXPIRATION AFTER OPENING</p>
                  <p class="mt-0.5 text-sm text-gray-800">{{ expirationVal(regData?.overview?.expiration?.expiration_after_opening, regData?.overview?.expiration?.expiration_after_opening_unit) }}</p>
                </div>
              </div>
            </section>

            <!-- Allergens -->
            <section>
              <h3 class="text-sm font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-200">
                Allergens
              </h3>
              <div
                v-if="regData?.overview?.allergens"
                class="grid grid-cols-3 gap-x-6 gap-y-3"
              >
                <div v-for="(value, key) in regData.overview.allergens" :key="key">
                  <p class="text-xs font-semibold uppercase tracking-wide text-gray-400">{{ String(key).replace(/_/g, ' ').replace(/^contains allergen /, '') }}</p>
                  <p class="mt-0.5 text-sm" :class="value === true ? 'text-red-600 font-medium' : 'text-gray-800'">{{ value === true ? 'Yes' : value === false ? 'No' : val(value) }}</p>
                </div>
              </div>
              <p v-else class="text-sm text-gray-400 italic">No allergen data</p>
            </section>

            <!-- Verification -->
            <section>
              <h3 class="text-sm font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-200">
                Verification
              </h3>
              <div>
                <p class="text-xs font-semibold uppercase tracking-wide text-gray-400">VERIFICATION SOURCE</p>
                <a
                  v-if="regData?.overview?.verification?.verification_source_url"
                  :href="regData.overview.verification.verification_source_url"
                  target="_blank"
                  rel="noopener"
                  class="mt-0.5 text-sm text-primary hover:underline inline-flex items-center gap-1"
                >
                  {{ regData.overview.verification.verification_source_url }}
                  <Icon name="OpenInNewTab" size-class="h-3 w-3" color="text-primary" />
                </a>
                <p v-else class="mt-0.5 text-sm text-gray-800">--</p>
              </div>
            </section>
          </template>

          <!-- === NUTRITION === -->
          <template v-if="activeTab === 'nutrition'">
            <!-- Caloric Information -->
            <section>
              <h3 class="text-sm font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-200">
                Caloric Information
              </h3>
              <div class="grid grid-cols-2 gap-x-8 gap-y-3">
                <div>
                  <p class="text-xs font-semibold uppercase tracking-wide text-gray-400">DISPLACEMENT FOR PWD (mL/g)</p>
                  <p class="mt-0.5 text-sm text-gray-800">{{ val(regData?.nutritions?.caloric_information?.displacement_for_pwd) }}</p>
                </div>
                <div>
                  <p class="text-xs font-semibold uppercase tracking-wide text-gray-400">OSMOLALITY (MOSM/KG)</p>
                  <p class="mt-0.5 text-sm text-gray-800">{{ val(regData?.nutritions?.caloric_information?.osmolality) }}</p>
                </div>
                <div>
                  <p class="text-xs font-semibold uppercase tracking-wide text-gray-400">OSMOLARITY (MOSM/L)</p>
                  <p class="mt-0.5 text-sm text-gray-800">{{ val(regData?.nutritions?.caloric_information?.osmolarity) }}</p>
                </div>
                <div>
                  <p class="text-xs font-semibold uppercase tracking-wide text-gray-400">PRSL (MOSM PER 100 KCAL)</p>
                  <p class="mt-0.5 text-sm text-gray-800">{{ val(regData?.nutritions?.caloric_information?.prsl) }}</p>
                </div>
                <div>
                  <p class="text-xs font-semibold uppercase tracking-wide text-gray-400">RENAL SOLUTE LOAD (MOSM/L)</p>
                  <p class="mt-0.5 text-sm text-gray-800">{{ val(regData?.nutritions?.caloric_information?.renal_solute_load) }}</p>
                </div>
                <div>
                  <p class="text-xs font-semibold uppercase tracking-wide text-gray-400">VOLUME TO PROVIDE 100 KCAL (mL)</p>
                  <p class="mt-0.5 text-sm text-gray-800">{{ val(regData?.nutritions?.caloric_information?.volume_to_provide_100_kcal) }}</p>
                </div>
                <div>
                  <p class="text-xs font-semibold uppercase tracking-wide text-gray-400">WATER (G)</p>
                  <p class="mt-0.5 text-sm text-gray-800">{{ val(regData?.nutritions?.caloric_information?.water) }}</p>
                </div>
              </div>
            </section>

            <!-- Protein -->
            <section>
              <h3 class="text-sm font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-200">
                Protein
              </h3>
              <div class="grid grid-cols-2 gap-x-8 gap-y-3">
                <div>
                  <p class="text-xs font-semibold uppercase tracking-wide text-gray-400">PROTEIN (G)</p>
                  <p class="mt-0.5 text-sm text-gray-800">{{ val(regData?.nutritions?.protein?.protein) }}</p>
                </div>
                <div>
                  <p class="text-xs font-semibold uppercase tracking-wide text-gray-400">PROTEIN SOURCE</p>
                  <p class="mt-0.5 text-sm text-gray-800">{{ val(regData?.nutritions?.protein?.protein_source) }}</p>
                </div>
                <div>
                  <p class="text-xs font-semibold uppercase tracking-wide text-gray-400">PROTEIN HYDROLYSIS</p>
                  <p class="mt-0.5 text-sm text-gray-800">{{ val(regData?.nutritions?.protein?.protein_hydrolysis) }}</p>
                </div>
                <div>
                  <p class="text-xs font-semibold uppercase tracking-wide text-gray-400">BCAA (G)</p>
                  <p class="mt-0.5 text-sm text-gray-800">{{ val(regData?.nutritions?.protein?.bcaa) }}</p>
                </div>
                <div>
                  <p class="text-xs font-semibold uppercase tracking-wide text-gray-400">TOTAL N2 (G)</p>
                  <p class="mt-0.5 text-sm text-gray-800">{{ val(regData?.nutritions?.protein?.total_n2) }}</p>
                </div>
              </div>
            </section>

            <!-- Fat -->
            <section>
              <h3 class="text-sm font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-200">
                Fat
              </h3>
              <div class="grid grid-cols-2 gap-x-8 gap-y-3">
                <div v-for="(value, key) in (regData?.nutritions?.fat ?? {})" :key="key">
                  <p class="text-xs font-semibold uppercase tracking-wide text-gray-400">{{ String(key).replace(/_/g, ' ') }}</p>
                  <p class="mt-0.5 text-sm text-gray-800">{{ val(value) }}</p>
                </div>
              </div>
              <p v-if="!regData?.nutritions?.fat" class="text-sm text-gray-400 italic">No data</p>
            </section>

            <!-- Carbohydrates -->
            <section>
              <h3 class="text-sm font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-200">
                Carbohydrates
              </h3>
              <div class="grid grid-cols-2 gap-x-8 gap-y-3">
                <div v-for="(value, key) in (regData?.nutritions?.carbohydrates ?? {})" :key="key">
                  <p class="text-xs font-semibold uppercase tracking-wide text-gray-400">{{ String(key).replace(/_/g, ' ') }}</p>
                  <p class="mt-0.5 text-sm text-gray-800">{{ val(value) }}</p>
                </div>
              </div>
              <p v-if="!regData?.nutritions?.carbohydrates" class="text-sm text-gray-400 italic">No data</p>
            </section>

            <!-- Serving Information -->
            <section>
              <h3 class="text-sm font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-200">
                Serving Information
              </h3>
              <div class="grid grid-cols-2 gap-x-8 gap-y-3">
                <div v-for="(value, key) in (regData?.nutritions?.serving_information ?? {})" :key="key">
                  <p class="text-xs font-semibold uppercase tracking-wide text-gray-400">{{ String(key).replace(/_/g, ' ') }}</p>
                  <p class="mt-0.5 text-sm text-gray-800">{{ val(value) }}</p>
                </div>
              </div>
              <p v-if="!regData?.nutritions?.serving_information" class="text-sm text-gray-400 italic">No data</p>
            </section>

            <!-- Ingredients -->
            <section>
              <h3 class="text-sm font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-200">
                Ingredients
              </h3>
              <p class="text-sm text-gray-800 whitespace-pre-wrap">{{ val(regData?.nutritions?.ingredients?.full_ingredients_list) }}</p>
            </section>

            <!-- Dietary Information -->
            <section>
              <h3 class="text-sm font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-200">
                Dietary Information
              </h3>
              <div class="grid grid-cols-3 gap-x-6 gap-y-3">
                <div>
                  <p class="text-xs font-semibold uppercase tracking-wide text-gray-400">GLUTEN-FREE</p>
                  <p class="mt-0.5 text-sm text-gray-800">{{ val(regData?.nutritions?.dietary_information?.gluten_free) }}</p>
                </div>
                <div>
                  <p class="text-xs font-semibold uppercase tracking-wide text-gray-400">HALAL</p>
                  <p class="mt-0.5 text-sm text-gray-800">{{ val(regData?.nutritions?.dietary_information?.halal) }}</p>
                </div>
                <div>
                  <p class="text-xs font-semibold uppercase tracking-wide text-gray-400">KOSHER</p>
                  <p class="mt-0.5 text-sm text-gray-800">{{ val(regData?.nutritions?.dietary_information?.kosher) }}</p>
                </div>
              </div>
            </section>
          </template>

          <!-- === VITAMINS === -->
          <template v-if="activeTab === 'vitamins'">
            <section v-for="(sectionData, sectionKey) in (regData?.vitamins ?? {})" :key="sectionKey">
              <h3 class="text-sm font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-200">
                {{ String(sectionKey).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) }}
              </h3>
              <div class="grid grid-cols-3 gap-x-6 gap-y-3">
                <div v-for="(value, key) in sectionData" :key="key">
                  <p class="text-xs font-semibold uppercase tracking-wide text-gray-400">{{ String(key).replace(/_/g, ' ') }}</p>
                  <p class="mt-0.5 text-sm text-gray-800">{{ val(value) }}</p>
                </div>
              </div>
            </section>
            <p v-if="!regData?.vitamins" class="text-sm text-gray-400 italic">No vitamin data</p>
          </template>

          <!-- === MINERALS === -->
          <template v-if="activeTab === 'minerals'">
            <section v-for="(sectionData, sectionKey) in (regData?.minerals ?? {})" :key="sectionKey">
              <h3 class="text-sm font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-200">
                {{ String(sectionKey).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) }}
              </h3>
              <div class="grid grid-cols-3 gap-x-6 gap-y-3">
                <div v-for="(value, key) in sectionData" :key="key">
                  <p class="text-xs font-semibold uppercase tracking-wide text-gray-400">{{ String(key).replace(/_/g, ' ') }}</p>
                  <p class="mt-0.5 text-sm text-gray-800">{{ val(value) }}</p>
                </div>
              </div>
            </section>
            <p v-if="!regData?.minerals" class="text-sm text-gray-400 italic">No mineral data</p>
          </template>

          <!-- === AMINO ACIDS === -->
          <template v-if="activeTab === 'amino_acids'">
            <section v-for="(sectionData, sectionKey) in (regData?.amino_acids ?? {})" :key="sectionKey">
              <h3 class="text-sm font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-200">
                {{ String(sectionKey).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) }}
              </h3>
              <div class="grid grid-cols-3 gap-x-6 gap-y-3">
                <div v-for="(value, key) in sectionData" :key="key">
                  <p class="text-xs font-semibold uppercase tracking-wide text-gray-400">{{ String(key).replace(/_/g, ' ') }}</p>
                  <p class="mt-0.5 text-sm text-gray-800">{{ val(value) }}</p>
                </div>
              </div>
            </section>
            <p v-if="!regData?.amino_acids" class="text-sm text-gray-400 italic">No amino acid data</p>
          </template>

          <!-- === PACKAGING === -->
          <template v-if="activeTab === 'packaging'">
            <section
              v-for="(sectionData, sectionKey) in (regData?.packaging ?? {})"
              v-show="sectionKey !== 'primary_package'"
              :key="sectionKey"
            >
              <h3 class="text-sm font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-200">
                {{ String(sectionKey).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) }}
              </h3>
              <div class="grid grid-cols-2 gap-x-8 gap-y-3">
                <div v-for="(value, key) in sectionData" :key="key">
                  <p class="text-xs font-semibold uppercase tracking-wide text-gray-400">{{ String(key).replace(/_/g, ' ') }}</p>
                  <p class="mt-0.5 text-sm text-gray-800">{{ val(String(value ?? '')) }}</p>
                </div>
              </div>
            </section>
            <p v-if="!regData?.packaging" class="text-sm text-gray-400 italic">
              <template v-if="product?.storageRequirements">
                <span class="text-xs font-semibold uppercase tracking-wide text-gray-400 block">STORAGE REQUIREMENTS</span>
                <span class="text-sm text-gray-800">{{ product.storageRequirements }}</span>
              </template>
              <template v-else>No packaging data</template>
            </p>

            <!-- Description -->
            <section>
              <h3 class="text-sm font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-200">
                Product Description
              </h3>
              <div class="grid grid-cols-2 gap-x-8 gap-y-4">
                <div v-if="regData?.overview?.description?.product_description || product?.description" class="col-span-2">
                  <p class="text-xs font-semibold uppercase tracking-wide text-gray-400">PRODUCT DESCRIPTION</p>
                  <p class="mt-0.5 text-sm text-gray-800 whitespace-pre-wrap">{{ val(regData?.overview?.description?.product_description ?? product?.description) }}</p>
                </div>
                <div v-if="regData?.overview?.description?.formula_classification?.length">
                  <p class="text-xs font-semibold uppercase tracking-wide text-gray-400">FORMULA CLASSIFICATION</p>
                  <p class="mt-0.5 text-sm text-gray-800">{{ Array.isArray(regData.overview.description.formula_classification) ? regData.overview.description.formula_classification.join(', ') : val(regData.overview.description.formula_classification) }}</p>
                </div>
                <div v-if="regData?.overview?.description?.parenteral_category">
                  <p class="text-xs font-semibold uppercase tracking-wide text-gray-400">PARENTERAL CATEGORY</p>
                  <p class="mt-0.5 text-sm text-gray-800">{{ val(regData.overview.description.parenteral_category) }}</p>
                </div>
                <div v-if="regData?.overview?.description?.substitutions" class="col-span-2">
                  <p class="text-xs font-semibold uppercase tracking-wide text-gray-400">SUBSTITUTIONS</p>
                  <p class="mt-0.5 text-sm text-gray-800 whitespace-pre-wrap">{{ val(regData.overview.description.substitutions) }}</p>
                </div>
              </div>
            </section>
          </template>

          <!-- === OTHER === -->
          <template v-if="activeTab === 'other'">
            <section v-for="(sectionData, sectionKey) in (regData?.other ?? {})" :key="sectionKey">
              <h3 class="text-sm font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-200">
                {{ String(sectionKey).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) }}
              </h3>
              <div class="grid grid-cols-2 gap-x-8 gap-y-3">
                <div v-for="(value, key) in sectionData" :key="key">
                  <p class="text-xs font-semibold uppercase tracking-wide text-gray-400">{{ String(key).replace(/_/g, ' ') }}</p>
                  <p class="mt-0.5 text-sm text-gray-800">{{ val(value) }}</p>
                </div>
              </div>
            </section>
            <p v-if="!regData?.other" class="text-sm text-gray-400 italic">No other data</p>
          </template>

          <!-- === HISTORY === -->
          <template v-if="activeTab === 'history'">
            <section>
              <h3 class="text-sm font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-200">
                Change History
              </h3>
              <div class="grid grid-cols-2 gap-x-8 gap-y-4">
                <div>
                  <p class="text-xs font-semibold uppercase tracking-wide text-gray-400">CREATED AT</p>
                  <p class="mt-0.5 text-sm text-gray-800">{{ product?.createdAt ? formatDate(product.createdAt) : '--' }}</p>
                </div>
                <div>
                  <p class="text-xs font-semibold uppercase tracking-wide text-gray-400">UPDATED AT</p>
                  <p class="mt-0.5 text-sm text-gray-800">{{ product?.updatedAt ? formatDate(product.updatedAt) : '--' }}</p>
                </div>
              </div>
              <p class="mt-4 text-sm text-gray-400 italic">
                Full change history will be available when connected to the backend.
              </p>
            </section>
          </template>
        </div>
      </div>
    </div>
  </DialogModal>

  <!-- Enter Image URL Dialog -->
  <DialogModal
    :visible="showUrlDialog"
    title="Enter Image URL"
    max-width="max-w-md"
    position="center"
    @update:visible="showUrlDialog = $event"
  >
    <div class="space-y-3">
      <p class="text-sm text-gray-600">
        Paste the URL of an image to upload. Supported formats: JPEG, PNG, WebP, GIF (max 5 MB).
      </p>
      <InputText
        v-model="imageUrlInput"
        class="w-full"
        placeholder="https://example.com/product-image.jpg"
        @keydown.enter="submitImageUrl"
      />
    </div>
    <template #footer>
      <div class="w-full flex justify-end gap-2">
        <Button
          severity="secondary"
          outlined
          label="Cancel"
          @click="showUrlDialog = false"
        />
        <Button
          severity="primary"
          label="Upload"
          :disabled="!imageUrlInput.trim()"
          :loading="isUploadingImage"
          @click="submitImageUrl"
        />
      </div>
    </template>
  </DialogModal>
</template>
